import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { API } from '../config/api';
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import {
  FileText, Check, ChevronDown, Pen, Type, Loader2,
  Shield, ScrollText, AlertCircle, CheckCircle2, Clock
} from "lucide-react";
import { LOGO_URL, KOI_BG_URL } from "../config/constants";

const koiStyle = {
  position: "fixed",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  opacity: 0.18,
  zIndex: 0,
  pointerEvents: "none",
  userSelect: "none",
};

const Shell = ({ children, centered = false }) => (
  <div className="relative min-h-screen bg-navy">
    <img src={KOI_BG_URL} alt="" aria-hidden="true" style={koiStyle} />
    <div className={`relative z-10 ${centered ? "flex items-center justify-center min-h-screen p-4" : ""}`}>
      {children}
    </div>
  </div>
);

export function SignatureCanvas({ onSave }) {
  const canvasRef = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const isDrawingRef = useRef(false);
  const hasDrawnRef = useRef(false);
  const activePointerIdRef = useRef(null);

  const getPoint = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const startDraw = useCallback((e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const point = getPoint(e);
    if (!canvas || !ctx || !point) return;

    e.preventDefault();
    isDrawingRef.current = true;
    hasDrawnRef.current = true;
    activePointerIdRef.current = e.pointerId;
    setHasDrawn(true);
    canvas.setPointerCapture?.(e.pointerId);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  }, [getPoint]);

  const draw = useCallback((e) => {
    if (!isDrawingRef.current || e.pointerId !== activePointerIdRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const point = getPoint(e);
    if (!ctx || !point) return;

    e.preventDefault();
    ctx.lineTo(point.x, point.y);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
  }, [getPoint]);

  const endDraw = useCallback((e) => {
    if (!isDrawingRef.current || e.pointerId !== activePointerIdRef.current) return;

    e.preventDefault();
    isDrawingRef.current = false;
    activePointerIdRef.current = null;
    if (hasDrawnRef.current && canvasRef.current) {
      onSave(canvasRef.current.toDataURL("image/png"));
    }
  }, [onSave]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const previousTouchAction = canvas.style.touchAction;
    canvas.style.touchAction = "none";
    const listenerOptions = { passive: false };

    canvas.addEventListener("pointerdown", startDraw, listenerOptions);
    canvas.addEventListener("pointermove", draw, listenerOptions);
    canvas.addEventListener("pointerup", endDraw, listenerOptions);
    canvas.addEventListener("pointercancel", endDraw, listenerOptions);
    canvas.addEventListener("lostpointercapture", endDraw, listenerOptions);

    return () => {
      canvas.removeEventListener("pointerdown", startDraw, listenerOptions);
      canvas.removeEventListener("pointermove", draw, listenerOptions);
      canvas.removeEventListener("pointerup", endDraw, listenerOptions);
      canvas.removeEventListener("pointercancel", endDraw, listenerOptions);
      canvas.removeEventListener("lostpointercapture", endDraw, listenerOptions);
      canvas.style.touchAction = previousTouchAction;
    };
  }, [draw, endDraw, startDraw]);

  const clear = () => {
    const ctx = canvasRef.current?.getContext("2d"); if (!ctx) return;
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    hasDrawnRef.current = false;
    setHasDrawn(false);
    onSave(null);
  };

  return (
    <div data-testid="signature-canvas-container">
      <canvas ref={canvasRef} width={500} height={150}
        className="signature-paper w-full border-2 border-dashed border-navy-border rounded-md cursor-crosshair touch-none"
        data-testid="signature-canvas" />
      {hasDrawn && <button onClick={clear} className="mt-2 text-xs text-teal hover:underline" data-testid="clear-signature-btn">Clear signature</button>}
    </div>
  );
}

function PolicySection({ doc, index }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-navy-border rounded-md overflow-hidden" data-testid={`policy-section-${index}`}>
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-navy/80 hover:bg-navy-hover transition-colors text-left">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-teal" />
          <div>
            <div className="font-medium text-white text-sm">{doc.title}</div>
            <div className="text-xs text-slate-500">{doc.name}</div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="p-4 bg-navy border-t border-navy-border">
          <p className="text-sm text-slate-400 leading-relaxed">{doc.description}</p>
          <div className="mt-3 p-4 bg-navy-hover/60 rounded-md border border-navy-border text-center">
            <FileText className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-xs text-slate-500">This document is included as part of your client care pack.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SigningPage() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sigMode, setSigMode] = useState("draw");
  const [typedName, setTypedName] = useState("");
  const [drawnSig, setDrawnSig] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signResult, setSignResult] = useState(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  // BM-H482c, 10 Sep 2026. Agreeing the client care letter is a real
  // acceptance, with the same elements the Terms of Business acceptance takes
  // at onboarding: the letter read to the end, an explicit confirmation, the
  // signer's own name, and a typed signature tested against that name.
  const [readConfirmed, setReadConfirmed] = useState(false);
  const cclRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/signing/${token}`);
        setSession(res.data);
        if (res.data.status === "signed") setSigned(true);
      } catch (err) {
        setError(errorMessage(err, "Failed to load signing page."));
      } finally { setLoading(false); }
    })();
  }, [token]);

  const handleScroll = useCallback((e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) setScrolledToEnd(true);
  }, []);

  const handleSign = async () => {
    if (!signerName.trim() || !readConfirmed) return;
    const sigData = sigMode === "draw" ? drawnSig : typedName;
    if (!sigData) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/signing/${token}/sign`, {
        signature_data: sigData, signature_type: sigMode,
        signer_name: signerName.trim(), signer_email: signerEmail.trim() || null,
        letter_read_confirmed: readConfirmed,
      });
      setSigned(true); setSignResult(res.data);
    } catch (err) { alert(errorMessage(err, "Failed to submit signature.")); }
    finally { setSubmitting(false); }
  };

  // The typed signature is compared on letters only, the same test the Terms
  // of Business acceptance and the director's guarantee apply, so spacing and
  // punctuation do not defeat it.
  const lettersOnly = (value) => (value || "").trim().toLowerCase().replace(/[^a-z]/g, "");
  const typedNameMatches = lettersOnly(typedName) === lettersOnly(signerName);
  const hasValidSig = sigMode === "draw"
    ? !!drawnSig
    : typedName.trim().length > 1 && typedNameMatches;

  if (loading) return (
    <Shell centered>
      <Loader2 className="w-8 h-8 text-teal animate-spin" />
    </Shell>
  );

  if (error) return (
    <Shell centered>
      <div className="max-w-md text-center space-y-4">
        <AlertCircle className="w-12 h-12 text-status-red mx-auto" />
        <h1 className="text-xl font-semibold text-white">Unable to Load</h1>
        <p className="text-slate-400">{error}</p>
      </div>
    </Shell>
  );

  if (session?.status === "expired") return (
    <Shell centered>
      <div className="max-w-md text-center space-y-4">
        <Clock className="w-12 h-12 text-status-amber mx-auto" />
        <h1 className="text-xl font-semibold text-white">Link Expired</h1>
        <p className="text-slate-400">{session.message}</p>
      </div>
    </Shell>
  );

  if (signed) return (
    <Shell centered>
      <div className="max-w-lg w-full text-center space-y-6 bg-navy/80 backdrop-blur-sm rounded-md border border-teal/40 shadow-lg p-10" data-testid="signing-confirmation">
        <div className="w-16 h-16 bg-teal-dim/40 rounded-md flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-teal" />
        </div>
        <h1 className="text-2xl font-bold text-teal">Document Signed</h1>
        <p className="text-slate-400">
          Thank you{(signResult?.signer_name || session?.signer_name) ? `, ${signResult?.signer_name || session?.signer_name}` : ""}. Your Client Care Letter for matter{" "}
          <span className="font-semibold text-white">{signResult?.matter_reference || session?.matter_reference}</span> has been signed successfully.
        </p>
        <div className="text-xs text-slate-500 space-y-1">
          <p>A copy of the signed document will be kept on file.</p>
          <p>If you have any questions, please contact your solicitor.</p>
        </div>
        <div className="pt-4 flex items-center justify-center gap-2">
          <img src={LOGO_URL} alt="Bonsai Law" className="w-6 h-6" />
          <span className="text-sm font-medium text-teal">Bonsai Law</span>
        </div>
      </div>
    </Shell>
  );

  return (
    <Shell>
      {/* Header */}
      <nav className="sticky top-0 z-50 w-full border-b border-navy-border bg-navy/90 backdrop-blur-sm shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Bonsai Law" className="w-9 h-9" />
            <div>
              <span className="text-sm font-bold text-teal block">Bonsai Law</span>
              <span className="text-xs text-slate-500">Client Care Letter</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-teal" />
            <span className="text-xs text-teal font-medium">Secure Document</span>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Welcome */}
        <div className="text-center space-y-2 glass-card p-6" data-testid="signing-welcome">
          <h1 className="text-2xl font-bold text-white">Client Care Letter</h1>
          <p className="text-slate-400">Dear <span className="font-medium text-white">{session.client_name}</span>, please review the documents below and sign to confirm your agreement.</p>
          <p className="text-xs text-slate-500">Ref: {session.matter_reference} &middot; Prepared by {session.fee_earner_name}</p>
        </div>

        {/* CCL Content */}
        <div className="glass-card overflow-hidden" data-testid="ccl-document-container">
          <div className="px-5 py-4 border-b border-navy-border flex items-center gap-3">
            <ScrollText className="w-5 h-5 text-teal" />
            <h2 className="font-semibold text-teal text-sm">Client Care Letter</h2>
            {!scrolledToEnd && (
              <span className="ml-auto text-xs text-status-amber flex items-center gap-1">
                <ChevronDown className="w-3 h-3 animate-bounce" /> Scroll to read
              </span>
            )}
          </div>
          <div ref={cclRef} onScroll={handleScroll}
            className="p-6 sm:p-8 max-h-[500px] overflow-y-auto prose prose-sm prose-invert max-w-none"
            data-testid="ccl-content"
            dangerouslySetInnerHTML={{ __html: session.ccl_html }} />
        </div>

        {/* Policy Documents */}
        <div data-testid="policy-documents-section">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-5 h-5 text-teal" />
            <h2 className="font-semibold text-white text-sm">Enclosed Documents ({session.policy_documents?.length || 0})</h2>
          </div>
          <div className="space-y-2">
            {session.policy_documents?.map((doc, i) => <PolicySection key={i} doc={doc} index={i} />)}
          </div>
        </div>

        {/* BM-H618 (25 Sep 2026): the firm's signature is in the letter itself,
            under "Yours sincerely" and above the fee earner's name, title and
            the firm (served that way by GET /api/signing/{token}). The separate
            "Signed on behalf of the Firm" box that repeated it is gone. */}

        {/* Client Signature */}
        <div className="glass-card border-2 border-teal/30 p-6 space-y-5" data-testid="client-signature-section">
          <h2 className="font-semibold text-teal text-sm">Your Signature</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1">Full Name *</label>
              <input type="text" value={signerName} onChange={(e) => setSignerName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full px-3 py-2.5 text-sm border border-navy-border rounded-md bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-teal/30"
                data-testid="signer-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1">Email (optional)</label>
              <input type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-3 py-2.5 text-sm border border-navy-border rounded-md bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-teal/30"
                data-testid="signer-email-input" />
            </div>
          </div>
          <div className="flex gap-2">
            {["draw", "type"].map(mode => (
              <button key={mode} onClick={() => setSigMode(mode)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  sigMode === mode ? "bg-teal text-white" : "bg-white/5 text-slate-400 hover:bg-white/10 border border-navy-border"
                }`} data-testid={`sig-mode-${mode}`}>
                {mode === "draw" ? <Pen className="w-4 h-4" /> : <Type className="w-4 h-4" />}
                {mode === "draw" ? "Draw" : "Type"}
              </button>
            ))}
          </div>
          {sigMode === "draw" ? (
            <SignatureCanvas onSave={setDrawnSig} />
          ) : (
            <div>
              <input type="text" value={typedName} onChange={(e) => setTypedName(e.target.value)}
                placeholder="Type your full name as signature"
                className="signature-paper w-full px-4 py-4 text-2xl font-sans italic border-2 border-dashed border-navy-border rounded-md focus:outline-none focus:ring-2 focus:ring-teal/30 text-center"
                data-testid="typed-signature-input" />
              {typedName && typedNameMatches && <p className="text-center mt-2 text-xs text-slate-500">Your typed name will be used as your electronic signature</p>}
              {typedName.trim() !== "" && !typedNameMatches && (
                <p className="text-center mt-2 text-xs text-status-amber" data-testid="typed-signature-mismatch">
                  Your typed signature should match your full name as entered above.
                </p>
              )}
            </div>
          )}
          <div className="p-3 bg-navy-hover/60 rounded-md border border-navy-border text-xs text-slate-400 leading-relaxed">
            By signing below, I confirm that I have read and understood the Client Care Letter and the enclosed documents. I agree to the terms of engagement set out therein. This electronic signature is legally binding.
          </div>
          {!scrolledToEnd && (
            <p className="text-xs text-status-amber" data-testid="letter-scroll-prompt">
              Please scroll to the bottom of the Client Care Letter to continue
            </p>
          )}
          <div className="flex items-start gap-2">
            <input type="checkbox" id="letter-read-confirm" checked={readConfirmed}
              onChange={(e) => setReadConfirmed(e.target.checked)}
              disabled={!scrolledToEnd}
              className="mt-1 h-4 w-4 rounded border-navy-border bg-white/5 accent-teal disabled:opacity-40"
              data-testid="letter-read-checkbox" />
            <label htmlFor="letter-read-confirm"
              className={`text-sm leading-snug ${scrolledToEnd ? "text-txt" : "text-slate-500"}`}>
              I confirm that I have read the Client Care Letter in full for matter {session.matter_reference}.
            </label>
          </div>
          <Button onClick={handleSign} disabled={submitting || !signerName.trim() || !hasValidSig || !readConfirmed}
            className="w-full py-6 text-base bg-teal hover:bg-teal-hover text-white rounded-md shadow-md transition-all disabled:opacity-50 gap-2"
            data-testid="submit-signature-btn">
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Signing...</> : <><Check className="w-5 h-5" /> Sign Document</>}
          </Button>
        </div>

        <div className="text-center text-xs text-slate-600 space-y-1 pt-4 pb-8">
          <p>This is a secure document from Bonsai Law. Your signature and IP address will be recorded for verification.</p>
          <p>Bonsai Law is authorised and regulated by the Solicitors Regulation Authority.</p>
        </div>
      </main>
    </Shell>
  );
}
